import type { ApiError, ApiResult, ErrorBody } from './errors';
import { mapApiError } from './errors';
import type { Authentication, TokenPair } from './models';
import { activeAccessToken, isChildActive, TokenStore } from './tokenStore';

/**
 * Ported from frontend/shared/src/commonMain/kotlin/sk/myslikrok/shared/Api/MysliKrokApi.kt.
 * A change to the Kotlin changes this file in the same commit — client code manifest §2a.
 *
 * The only thing in the web client that talks to the client API. No screen opens a request of its own.
 *
 * Token refresh is **single-in-flight**. Refresh tokens rotate, and presenting an already-rotated one is
 * treated by the server as theft and revokes the entire chain (ADR-0005) — so two concurrent 401s must
 * share one refresh, or the loser signs the user out of every device. Kotlin uses a mutex; here the
 * in-flight promise is the lock, which is the same guarantee in a single-threaded runtime.
 */
export const API_BASE_URL: string =
  (import.meta.env.VITE_CLIENT_API_URL as string | undefined) ?? 'http://localhost:3000/api/v1';

type Method = 'GET' | 'POST' | 'PATCH';

let refreshInFlight: Promise<boolean> | null = null;
let reMintInFlight: Promise<boolean> | null = null;

/**
 * Called when child mode cannot be renewed and has to end. The shell sets this; the client does not
 * navigate, because a network layer that moves the user around is a network layer nobody can reason about.
 */
let onChildModeLost: (() => void) | null = null;

export const setChildModeLostHandler = (handler: (() => void) | null): void => {
  onChildModeLost = handler;
};

export async function request<T>(
  path: string,
  method: Method,
  body: unknown = null,
  authenticated = true,
  retryAfterRefresh = true,
): Promise<ApiResult<T>> {
  let response: Response;

  try {
    response = await send(path, method, body, authenticated);
  } catch (cause) {
    // The platform's own message, never the user's. A network failure never reaches the API, so it never
    // gets catalogue copy — and passing `cause.message` through put English written for a developer
    // straight on a screen: "Failed to fetch" in a browser, and on Android's release build a child saw
    // "CLEARTEXT communication to 10.0.2.2 not permitted by network security policy" (R0043).
    //
    // R0033 localised every error the API can answer with. This is the one it cannot.
    // eslint-disable-next-line no-console
    console.warn(`[api] Network failure on ${path}:`, cause);

    // design-system/06-Components/08-Lists-And-States.md §5 — offline is not the same failure as a
    // server that is simply down, and saying so changes what the reader does next: one is worth waiting
    // out, the other is not (R0120).
    //
    // Two whole sentences chosen as one constant, never concatenated. BR-13 forbids assembling a
    // user-facing sentence from fragments, and „message + hint" is exactly that assembly.
    const offline = typeof navigator !== 'undefined' && navigator.onLine === false;

    return {
      ok: false,
      error: {
        kind: 'network',
        offline,
        message: offline ? 'Nie si online. Skús to znova, keď budeš online.' : 'Sieť nie je dostupná.',
      },
    };
  }

  if (response.ok) {
    try {
      // 204 and an empty body are both legitimate — `register` and `logout` return nothing.
      const text = await response.text();

      return { ok: true, value: (text.length === 0 ? undefined : JSON.parse(text)) as T };
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : 'Odpoveď sa nepodarilo prečítať.';

      return { ok: false, error: { kind: 'server', message, correlationId: '' } };
    }
  }

  if (response.status === 401 && authenticated && retryAfterRefresh) {
    // Which credential expired decides what to do, and getting this wrong is what R0041 fixed.
    //
    // In child mode the answer is **never** a parent refresh. The active token is the child's, so
    // refreshing the parent chain rotates a refresh token for nothing and retries with the same dead
    // token — failing anyway, and leaving a rotated token behind. ADR-0005 treats a re-presented rotated
    // token as theft and revokes the whole chain, so the old behaviour could sign a family out of every
    // device to achieve nothing at all.
    const renewed = isChildActive() ? await reMintChildToken() : await refresh();

    if (renewed) return request<T>(path, method, body, authenticated, false);
  }

  const errorBody = await readErrorBody(response);

  return { ok: false, error: mapApiError(response.status, errorBody) };
}

async function send(path: string, method: Method, body: unknown, authenticated: boolean): Promise<Response> {
  const token = authenticated ? activeAccessToken() : null;
  const headers: Record<string, string> = { 'Accept-Language': 'sk' };

  if (token !== null) headers.Authorization = `Bearer ${token}`;
  if (method !== 'GET') headers['Content-Type'] = 'application/json';

  return fetch(API_BASE_URL + path, {
    method,
    headers,
    body: method === 'GET' || body === null ? undefined : JSON.stringify(body),
  });
}

async function readErrorBody(response: Response): Promise<ErrorBody | null> {
  try {
    return (await response.json()) as ErrorBody;
  } catch {
    return null;
  }
}

/**
 * Refreshes the parent token, once at a time. Resolves to whether a usable token now exists.
 *
 * A child-scoped token is deliberately **not** refreshable: it is short-lived and re-minted by switching
 * profile again, which keeps the parent's refresh chain out of child mode entirely.
 */
async function refresh(): Promise<boolean> {
  if (refreshInFlight !== null) return refreshInFlight;

  refreshInFlight = (async (): Promise<boolean> => {
    const refreshToken = TokenStore.readParentRefreshToken();

    if (refreshToken === null) return false;

    let response: Response;

    try {
      response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept-Language': 'sk' },
        body: JSON.stringify({ refreshToken }),
      });
    } catch {
      return false;
    }

    if (!response.ok) {
      TokenStore.clearAll();

      return false;
    }

    try {
      const authentication = (await response.json()) as Authentication;

      TokenStore.writeParentTokens(authentication.tokens.accessToken, authentication.tokens.refreshToken);

      return true;
    } catch {
      return false;
    }
  })().finally(() => {
    refreshInFlight = null;
  });

  return refreshInFlight;
}

/**
 * Issues a fresh child token from the parent's session, once at a time.
 *
 * **The deliberate trade (R0041).** This renews child mode without the parent being present, so after the
 * first hour "the parent opens child mode" stops being literally true. What it does *not* do is grant the
 * device anything new: the parent token was already in storage, and anything holding it could already have
 * minted a child token by hand. The boundary this protects is against a *child* reaching parent screens,
 * and that is enforced by the shell and by the API, neither of which this touches.
 *
 * The alternative — ending child mode on the hour — was rejected because it interrupts a child mid-task
 * for a reason no child can understand.
 */
async function reMintChildToken(): Promise<boolean> {
  if (reMintInFlight !== null) return reMintInFlight;

  reMintInFlight = (async (): Promise<boolean> => {
    const childId = TokenStore.readActiveChildId();

    if (childId === null) return false;

    const minted = await mintFor(childId);

    if (minted) return true;

    // The parent's access token may have expired too — it is the shorter-lived of the two in practice,
    // because a child session runs while nobody touches the parent's. Refresh it once and try again.
    if (!(await refresh())) return endChildMode();

    return (await mintFor(childId)) || endChildMode();
  })().finally(() => {
    reMintInFlight = null;
  });

  return reMintInFlight;
}

/** One attempt, explicitly on the **parent** token — `activeAccessToken` would hand back the dead one. */
async function mintFor(childId: string): Promise<boolean> {
  const parentToken = TokenStore.readParentAccessToken();

  if (parentToken === null) return false;

  try {
    const response = await fetch(`${API_BASE_URL}/auth/children/${childId}/token`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${parentToken}`, 'Accept-Language': 'sk' },
    });

    if (!response.ok) return false;

    const tokens = (await response.json()) as TokenPair;

    TokenStore.writeChildToken(childId, tokens.accessToken);

    return true;
  } catch {
    return false;
  }
}

/**
 * The parent's session is gone too, so child mode cannot continue.
 *
 * The token is cleared and the shell is told. A child is never shown an authentication error — that is a
 * sentence about credentials, written for an adult (BR-13).
 */
function endChildMode(): boolean {
  TokenStore.clearChildToken();
  onChildModeLost?.();

  return false;
}

/** Narrowing helper so a caller can read an error without repeating the discriminant everywhere. */
export const errorOf = <T>(result: ApiResult<T>): ApiError | null => (result.ok ? null : result.error);
